import { useCallback, useEffect, useState } from 'react';

import { Badge } from '@bambi/component-library/src/components/Badge/Badge';
import { Button } from '@bambi/component-library/src/components/Button/Button';
import {
  AdjustmentsHorizontalIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { capitalize } from 'lodash';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  AssignedDriverRead,
  DispatchTripPayerOrganizationRead,
  OverrideStatusEnum,
  SpaceTypeEnum,
  TagRead,
} from 'src/common/external/bambi-api/bambiApi';
import { FilterPopover } from 'src/common/primitives/FilterPopover/FilterPopover';
import { FilterCountBadge } from 'src/common/primitives/FilterPopover/FilterPopoverTrigger';
import { Tag } from 'src/common/tags/Tag/Tag';
import { formatFullName } from 'src/common/util/formatFullName';
import AccordionFilterItem from 'src/features/dispatch/common/AccordionFilterItem';
import { SpaceTypeFilter } from 'src/features/dispatch/common/SpaceTypeFilter';
import { TagFilter } from 'src/features/dispatch/common/TagFilter';
import { dispatcherSlice } from 'src/features/dispatch/dispatcher.slice';
import { selectNumberOfDispatchCalendarFiltersApplied } from 'src/features/dispatch/selectors';
import { TripStatusBadge } from 'src/features/trip/management/TripStatusBadge';


import DispatchCalendarAssignmentFilter from './DispatchCalendarAssignmentFilter';
import DispatchCalendarPayerFilter from './DispatchCalendarPayerFilter';
import { StatusFilter } from './StatusFilter';

export function DispatchCalendarFilters() {
  const dispatchCalendarFilters = useAppSelector(
    (state) => state.dispatcher.dispatchCalendarFilters
  );
  const [selectedDrivers, setSelectedDrivers] = useState<AssignedDriverRead[]>(
    dispatchCalendarFilters.drivers
  );
  const [selectedVehicleTags, setSelectedVehicleTags] = useState<TagRead[]>(
    dispatchCalendarFilters.vehicleTags
  );
  const [selectedDriverTags, setSelectedDriverTags] = useState<TagRead[]>(
    dispatchCalendarFilters.driverTags
  );
  const [selectedStatuses, setSelectedStatuses] = useState<
    OverrideStatusEnum[]
  >(dispatchCalendarFilters.statuses);

  const [selectedPayers, setSelectedPayers] = useState<
    DispatchTripPayerOrganizationRead[]
  >(dispatchCalendarFilters.payers);

  const [selectedSpaceTypes, setSelectedSpaceTypes] = useState<SpaceTypeEnum[]>(
    dispatchCalendarFilters.spaceTypes
  );

  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);

  const handleApply = useCallback(() => {
    dispatch(
      dispatcherSlice.actions.onDispatchCalendarFiltersApply({
        drivers: selectedDrivers,
        vehicleTags: selectedVehicleTags,
        driverTags: selectedDriverTags,
        statuses: selectedStatuses,
        payers: selectedPayers,
        spaceTypes: selectedSpaceTypes,
      })
    );
    setOpen(false);
  }, [
    dispatch,
    selectedDrivers,
    selectedDriverTags,
    selectedVehicleTags,
    selectedStatuses,
    selectedPayers,
    selectedSpaceTypes,
  ]);

  const handleClear = useCallback(() => {
    setSelectedDrivers([]);
    setSelectedVehicleTags([]);
    setSelectedDriverTags([]);
    setSelectedStatuses([]);
    setSelectedPayers([]);
    setSelectedSpaceTypes([]);
    dispatch(dispatcherSlice.actions.onDispatchCalendarFiltersClear());
  }, [dispatch]);

  const numFiltersApplied = useAppSelector(
    selectNumberOfDispatchCalendarFiltersApplied
  );

  useEffect(() => {
    if (!open) {
      setSelectedDrivers(dispatchCalendarFilters.drivers);
      setSelectedVehicleTags(dispatchCalendarFilters.vehicleTags);
      setSelectedDriverTags(dispatchCalendarFilters.driverTags);
      setSelectedStatuses(dispatchCalendarFilters.statuses);
      setSelectedPayers(dispatchCalendarFilters.payers);
      setSelectedSpaceTypes(dispatchCalendarFilters.spaceTypes);
    }
    // We only want to run this effect when the open state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className="flex items-center">
      <FilterPopover
        open={open}
        onOpenChange={setOpen}
        onApply={handleApply}
        onCancel={() => {
          setOpen(false);
        }}
        applyButtonText="Apply Filters"
        cancelButtonText="Cancel"
        contentClassName="flex max-h-[75vh] min-w-[300px] max-w-[80vw] flex-col justify-between px-1 md:max-w-[30vw]"
        triggerElement={
          <Button dataTestId="dispatch-calendar-filter-trigger">
            <AdjustmentsHorizontalIcon className="mr-2 h-4 w-4" />
            Filters
            <div className="flex items-center gap-1">
              {numFiltersApplied > 0 && (
                <FilterCountBadge value={numFiltersApplied} />
              )}
            </div>
          </Button>
        }
      >
        <div className="mb-4 flex flex-col gap-2">
          {/* Only show the applied filters section if there are filters applied */}
          {(selectedDrivers.length > 0 ||
            selectedVehicleTags.length > 0 ||
            selectedDriverTags.length > 0 ||
            selectedStatuses.length > 0 ||
            selectedPayers.length > 0 ||
            selectedSpaceTypes.length > 0) && (
            <div>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Applied Filters</h3>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSelectedDrivers([]);
                    setSelectedVehicleTags([]);
                    setSelectedDriverTags([]);
                    setSelectedStatuses([]);
                    setSelectedPayers([]);
                    setSelectedSpaceTypes([]);
                  }}
                  className="text-xs"
                  dataTestId="clear-all-dispatch-calendar-filters-button"
                  disabled={
                    selectedDrivers.length === 0 &&
                    selectedVehicleTags.length === 0 &&
                    selectedDriverTags.length === 0 &&
                    selectedStatuses.length === 0 &&
                    selectedPayers.length === 0 &&
                    selectedSpaceTypes.length === 0
                  }
                >
                  Clear All
                </Button>
              </div>
              <div
                data-testid="dispatch-calendar-applied-filters"
                className="flex flex-wrap gap-2"
              >
                {selectedDrivers.map((driver) => {
                  return (
                    <Badge
                      key={driver.id}
                      label={`Driver: ${formatFullName(driver)}`}
                      color="blue"
                      closeable
                      onClose={() => {
                        setSelectedDrivers((prev) =>
                          prev.filter((d) => d.id !== driver.id)
                        );
                      }}
                      testId={`driver-filter-${driver.id}`}
                    />
                  );
                })}
                {selectedStatuses.map((status) => {
                  return (
                    <TripStatusBadge
                      key={status}
                      status={status}
                      onDelete={() => {
                        setSelectedStatuses((prev) =>
                          prev.filter((s) => s !== status)
                        );
                      }}
                    />
                  );
                })}
                {selectedVehicleTags.map((tag) => {
                  return (
                    <Tag
                      key={tag.id}
                      label={`Vehicle Tag: ${tag.tag_name}`}
                      color={tag.color_hex || '#DDD'}
                      onClick={() => {
                        setSelectedVehicleTags((prev) =>
                          prev.filter((t) => t.id !== tag.id)
                        );
                      }}
                      icon={
                        <XMarkIcon
                          className="h-4 w-4 cursor-pointer"
                          data-testid={`delete-vehicle-tag-${tag.id}`}
                        />
                      }
                      data-testid={`vehicle-tag-${tag.id}`}
                    />
                  );
                })}
                {selectedDriverTags.map((tag) => {
                  return (
                    <Tag
                      key={tag.id}
                      label={`Driver Tag: ${tag.tag_name}`}
                      color={tag.color_hex || '#DDD'}
                      onClick={() => {
                        setSelectedDriverTags((prev) =>
                          prev.filter((t) => t.id !== tag.id)
                        );
                      }}
                      icon={
                        <XMarkIcon
                          className="h-4 w-4 cursor-pointer"
                          data-testid={`delete-driver-tag-${tag.id}`}
                        />
                      }
                      data-testid={`driver-tag-${tag.id}`}
                    />
                  );
                })}
                {selectedPayers.map((payer) => {
                  return (
                    <Badge
                      key={payer.id}
                      label={`Payer: ${payer.display_name}`}
                      color="yellow"
                      closeable
                      onClose={() => {
                        setSelectedPayers((prev) =>
                          prev.filter((p) => p.id !== payer.id)
                        );
                      }}
                      data-testid={`payer-${payer.id}`}
                    />
                  );
                })}
                {selectedSpaceTypes.map((spaceType) => {
                  return (
                    <Badge
                      key={spaceType}
                      label={`Space Type: ${capitalize(spaceType)}`}
                      color="blue"
                      closeable
                      onClose={() => {
                        setSelectedSpaceTypes((prev) =>
                          prev.filter((s) => s !== spaceType)
                        );
                      }}
                      testId={`space-type-filter-${spaceType}`}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <AccordionFilterItem
            badgeLabel={selectedDrivers.length}
            itemName="driver"
            label="Drivers"
          >
            <DispatchCalendarAssignmentFilter
              selectedDrivers={selectedDrivers}
              onChange={setSelectedDrivers}
            />
          </AccordionFilterItem>

          <AccordionFilterItem
            badgeLabel={selectedStatuses.length}
            itemName="status"
            label="Statuses"
          >
            <StatusFilter
              selectedStatuses={selectedStatuses}
              onStatusChange={setSelectedStatuses}
            />
          </AccordionFilterItem>

          <AccordionFilterItem
            badgeLabel={selectedVehicleTags.length}
            itemName="vehicle-tag"
            label="Vehicle Tags"
          >
            <TagFilter
              selectedTags={selectedVehicleTags}
              onTagChange={setSelectedVehicleTags}
              dataTestId="vehicle-tag-filter"
            />
          </AccordionFilterItem>

          <AccordionFilterItem
            badgeLabel={selectedDriverTags.length}
            itemName="driver-tag"
            label="Driver Tags"
          >
            <TagFilter
              selectedTags={selectedDriverTags}
              onTagChange={setSelectedDriverTags}
              dataTestId="driver-tag-filter"
            />
          </AccordionFilterItem>
          <AccordionFilterItem
            badgeLabel={selectedPayers.length}
            itemName="payer"
            label="Payers"
          >
            <DispatchCalendarPayerFilter
              selectedPayers={selectedPayers}
              onPayerChange={setSelectedPayers}
            />
          </AccordionFilterItem>
          <AccordionFilterItem
            badgeLabel={selectedSpaceTypes.length}
            itemName="space-type"
            label="Space Types"
          >
            <SpaceTypeFilter
              selectedSpaceTypeIds={selectedSpaceTypes}
              onSpaceTypeChange={setSelectedSpaceTypes}
            />
          </AccordionFilterItem>
        </div>
      </FilterPopover>
      {!!numFiltersApplied && (
        <Button
          onClick={handleClear}
          variant="anchor"
          dataTestId="clear-dispatch-calendar-filters-button"
        >
          clear filters
        </Button>
      )}
    </div>
  );
}
